import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  calculateCheckoutBreakdown,
  PAYMENT_PROCESSING_FEE_LABEL,
  SALES_TAX_FEE_LABEL,
  providerEarningsPercent,
} from '../utils/feeCalculation';

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

interface ProviderPriceBreakdownProps {
  servicePrice: number;
  platformFeePercent: number;
  /** Optional service label shown above the breakdown */
  label?: string;
  /** Extra rows above the fee lines (e.g. duration, frequency) */
  extraRows?: Array<{ label: string; value: string }>;
  compact?: boolean;
  /** Sales tax % (provider-configured). Always shown; use 0 for $0.00. */
  salesTaxPercent?: number | null;
  salesTaxState?: string | null;
}

/**
 * Provider-facing fee preview: service price, sales tax (to you), platform fee, provider earnings,
 * and what the customer pays including card processing (gross-up).
 */
const ProviderPriceBreakdown: React.FC<ProviderPriceBreakdownProps> = ({
  servicePrice,
  platformFeePercent,
  label,
  extraRows,
  compact,
  salesTaxPercent,
  salesTaxState,
}) => {
  if (!servicePrice || servicePrice <= 0) return null;

  const breakdown = calculateCheckoutBreakdown(servicePrice, platformFeePercent, {
    salesTaxPercent,
    salesTaxState,
  });
  const providerPct = providerEarningsPercent(platformFeePercent);
  const taxLabel = breakdown.salesTaxState
    ? `${SALES_TAX_FEE_LABEL} (${breakdown.salesTaxState}) — to you:`
    : `${SALES_TAX_FEE_LABEL} — to you:`;
  const earningsLabel =
    breakdown.salesTaxAmount > 0
      ? 'Your Earnings (service + tax):'
      : `Your Earnings (${providerPct}%):`;

  return (
    <Box
      sx={{
        mb: compact ? 1 : 2,
        p: compact ? 1.5 : 2,
        backgroundColor: 'white',
        borderRadius: 1,
        border: 1,
        borderColor: 'grey.200',
      }}
    >
      {label && (
        <Typography variant="body1" sx={{ fontWeight: 'bold', mb: 1 }}>
          {label}
        </Typography>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          Your Price:
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
          {formatCurrency(breakdown.servicePrice)}
        </Typography>
      </Box>

      {extraRows?.map((row) => (
        <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
          <Typography variant="body2" color="text.secondary">
            {row.label}
          </Typography>
          <Typography variant="body2">{row.value}</Typography>
        </Box>
      ))}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body2" color="text.secondary">
          {taxLabel}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formatCurrency(breakdown.salesTaxAmount)}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 0.5,
          borderTop: 1,
          borderColor: 'grey.300',
          pt: 0.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Platform Fee ({platformFeePercent}% of service):
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formatCurrency(breakdown.platformCut)}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
          {earningsLabel}
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'success.main', fontSize: compact ? '1rem' : '1.1rem' }}>
          {formatCurrency(breakdown.providerCut)}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          mb: 0.5,
          borderTop: 1,
          borderColor: 'grey.300',
          pt: 0.5,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {PAYMENT_PROCESSING_FEE_LABEL} (customer):
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {formatCurrency(breakdown.processingFee)}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="body1" sx={{ fontWeight: 600, color: 'primary.main' }}>
          Customer Pays (card total):
        </Typography>
        <Typography variant="body1" sx={{ fontWeight: 'bold', color: 'primary.main', fontSize: compact ? '1rem' : '1.1rem' }}>
          {formatCurrency(breakdown.totalCharge)}
        </Typography>
      </Box>
    </Box>
  );
};

export default ProviderPriceBreakdown;
